import React, { useState } from "react";
import { isValidHexColor, normalizeHexColor } from "@common/helpers/color";
import { Input } from "@design-system/Input/Input";
import styles from "./ColorPickerControl.module.scss";

interface Props {
    value: string;
    onChange: (hex: string) => void;
    ariaLabel: string;
    clearable?: boolean;
    onClear?: () => void;
    disabled?: boolean;
}

export const ColorPickerControl: React.FC<Props> = ({
    value,
    onChange,
    ariaLabel,
    clearable = false,
    onClear,
    disabled = false,
}) => {
    const [text, setText] = useState(value);
    const [prevValue, setPrevValue] = useState(value);
    if (value !== prevValue) {
        setPrevValue(value);
        const isEquivalentDraft =
            isValidHexColor(text) && normalizeHexColor(text) === value;
        if (!isEquivalentDraft) {
            setText(value);
        }
    }

    const handleTextChange = (next: string) => {
        setText(next);
        if (isValidHexColor(next)) {
            onChange(normalizeHexColor(next));
        }
    };

    return (
        <div className={styles.colorControl}>
            <label
                aria-label={ariaLabel}
                title={ariaLabel}
                className={styles.colorSwatch}
                style={{
                    background: isValidHexColor(value)
                        ? normalizeHexColor(value)
                        : undefined,
                }}
            >
                <input
                    type="color"
                    className={styles.colorInput}
                    aria-label={`${ariaLabel} palette`}
                    disabled={disabled}
                    value={
                        isValidHexColor(value)
                            ? normalizeHexColor(value)
                            : "#000000"
                    }
                    onChange={(event) => {
                        setText(event.target.value);
                        onChange(event.target.value);
                    }}
                />
            </label>
            <Input
                className={styles.hexField}
                size="md"
                type="text"
                clearable={clearable}
                onClear={onClear}
                disabled={disabled}
                aria-label={`${ariaLabel} hex`}
                value={text}
                spellCheck={false}
                maxLength={7}
                placeholder="#000000"
                onChange={(event) => handleTextChange(event.target.value)}
                onBlur={() => setText(value)}
            />
        </div>
    );
};
